import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { marketingContractService } from '../../../services/marketingContractService';
import ContractPreviewModal from './ContractPreviewModal';

const MarketingContractDetails = ({ contractId }) => {
  const [marketingContract, setMarketingContract] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  useEffect(() => {
    if (contractId) {
      loadMarketingContract();
    }
  }, [contractId]);

  const loadMarketingContract = async () => {
    setIsLoading(true);
    try {
      const data = await marketingContractService?.getMarketingContract(contractId);
      setMarketingContract(data);
    } catch (error) {
      console.error('Error loading marketing contract:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArddjoAcceptance = async (status) => {
    setIsUpdating(true);
    try {
      await marketingContractService?.arddjoAcceptance(marketingContract?.id, status);
      alert(status === 'accepted' ? 'تمت الموافقة من ARDDJO' : 'تم رفض العقد من ARDDJO');
      loadMarketingContract();
    } catch (error) {
      alert(error?.message || 'فشل في تحديث الموافقة');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleOwnerAcceptance = async (status) => {
    setIsUpdating(true);
    try {
      await marketingContractService?.ownerAcceptance(marketingContract?.id, status);
      alert(status === 'accepted' ? 'تمت الموافقة من المالك' : 'تم رفض العقد من المالك');
      loadMarketingContract();
    } catch (error) {
      alert(error?.message || 'فشل في تحديث الموافقة');
    } finally {
      setIsUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'قيد الانتظار', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
      accepted: { label: 'موافق عليه', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
      rejected: { label: 'مرفوض', color: 'bg-red-500/20 text-red-400 border-red-500/30' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${config?.color}`}>
        {config?.label}
      </span>
    );
  };

  const getChannelLabel = (channel) => {
    const labels = {
      social_media: 'وسائل التواصل',
      website: 'الموقع الإلكتروني',
      property_portals: 'بوابات العقارات',
      print_media: 'الإعلانات المطبوعة',
      email_marketing: 'البريد الإلكتروني',
      sms_marketing: 'الرسائل النصية'
    };
    return labels?.[channel] || channel;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Icon name="Loader" size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  if (!marketingContract) {
    return null;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/20 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="TrendingUp" size={20} className="text-purple-400" />
          <h3 className="text-lg font-heading font-semibold text-foreground">
            عقد تسويق عقاري
          </h3>
        </div>
        <p className="text-sm text-muted-foreground">
          عقد تسويق مع نسب عمولة مرنة لـ ARDDJO والمالك
        </p>
      </div>
      {/* Retainer Fees */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="DollarSign" size={16} />
          نسب ومبالغ العمولة
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">عمولة ARDDJO</p>
            <p className="text-lg font-bold text-blue-400">
              {marketingContract?.arddjo_retainer_percentage}%
            </p>
            <p className="text-sm text-foreground mt-1">
              {marketingContract?.arddjo_retainer_amount?.toLocaleString('ar-JO')} دينار
            </p>
          </div>
          <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">عمولة المالك</p>
            <p className="text-lg font-bold text-green-400">
              {marketingContract?.owner_retainer_percentage}%
            </p>
            <p className="text-sm text-foreground mt-1">
              {marketingContract?.owner_retainer_amount?.toLocaleString('ar-JO')} دينار
            </p>
          </div>
        </div>
      </div>
      {/* Marketing Details */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-4">تفاصيل التسويق</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">مدة التسويق</p>
            <p className="text-sm font-medium text-foreground">
              {marketingContract?.marketing_duration_months} أشهر
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">الحقوق الحصرية</p>
            <p className="text-sm font-medium text-foreground">
              {marketingContract?.exclusive_rights ? 'نعم - حصري' : 'لا - غير حصري'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">السعر المستهدف</p>
            <p className="text-sm font-medium text-foreground">
              {marketingContract?.target_price?.toLocaleString('ar-JO')} دينار
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground mb-1">الحد الأدنى للسعر</p>
            <p className="text-sm font-medium text-foreground">
              {marketingContract?.minimum_price?.toLocaleString('ar-JO')} دينار
            </p>
          </div>
        </div>

        {marketingContract?.marketing_channels?.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-2">قنوات التسويق</p>
            <div className="flex flex-wrap gap-2">
              {marketingContract?.marketing_channels?.map((channel, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-muted border border-border rounded-full text-xs font-medium text-foreground"
                >
                  {getChannelLabel(channel)}
                </span>
              ))}
            </div>
          </div>
        )}

        {marketingContract?.notes && (
          <div className="mt-4">
            <p className="text-xs text-muted-foreground mb-1">ملاحظات</p>
            <p className="text-sm text-foreground">{marketingContract?.notes}</p>
          </div>
        )}
      </div>
      {/* Acceptance Status */}
      <div className="bg-card border border-border rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="CheckCircle" size={16} />
          حالة الموافقة
        </h4>
        
        {/* ARDDJO Acceptance */}
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon name="Building" size={16} className="text-blue-400" />
              <span className="text-sm font-medium text-foreground">موافقة ARDDJO</span>
            </div>
            {getStatusBadge(marketingContract?.arddjo_acceptance_status)}
          </div>
          {marketingContract?.arddjo_acceptance_status === 'pending' && (
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleArddjoAcceptance('accepted')}
                disabled={isUpdating}
                className="flex-1"
              >
                <Icon name="Check" size={14} className="ml-1" />
                موافقة
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleArddjoAcceptance('rejected')}
                disabled={isUpdating}
                className="flex-1 text-red-400 border-red-500/30 hover:bg-red-500/10"
              >
                <Icon name="X" size={14} className="ml-1" />
                رفض
              </Button>
            </div>
          )}
          {marketingContract?.arddjo_accepted_at && (
            <p className="text-xs text-muted-foreground mt-2">
              تاريخ الموافقة: {new Date(marketingContract?.arddjo_accepted_at)?.toLocaleDateString('ar-JO')}
            </p>
          )}
        </div>

        {/* Owner Acceptance */}
        <div className="p-3 bg-muted rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Icon name="User" size={16} className="text-green-400" />
              <span className="text-sm font-medium text-foreground">موافقة المالك</span>
            </div>
            {getStatusBadge(marketingContract?.owner_acceptance_status)}
          </div>
          {marketingContract?.owner_acceptance_status === 'pending' && (
            <div className="flex gap-2 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleOwnerAcceptance('accepted')}
                disabled={isUpdating}
                className="flex-1"
              >
                <Icon name="Check" size={14} className="ml-1" />
                موافقة
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleOwnerAcceptance('rejected')}
                disabled={isUpdating}
                className="flex-1 text-red-400 border-red-500/30 hover:bg-red-500/10"
              >
                <Icon name="X" size={14} className="ml-1" />
                رفض
              </Button>
            </div>
          )}
          {marketingContract?.owner_accepted_at && (
            <p className="text-xs text-muted-foreground mt-2">
              تاريخ الموافقة: {new Date(marketingContract?.owner_accepted_at)?.toLocaleDateString('ar-JO')}
            </p>
          )}
        </div>
      </div>
      {/* Overall Status */}
      {marketingContract?.arddjo_acceptance_status === 'accepted' && 
       marketingContract?.owner_acceptance_status === 'accepted' && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
          <div className="flex items-center gap-2">
            <Icon name="CheckCircle" size={20} className="text-green-400" />
            <p className="text-sm font-semibold text-green-400">
              تمت الموافقة على عقد التسويق من جميع الأطراف
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 mb-6">
        <Button
          onClick={() => setShowPreviewModal(true)}
          className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
        >
          <Icon name="Eye" size={18} className="ml-2" />
          معاينة وطباعة العقد
        </Button>
      </div>
      
      {/* Preview Modal */}
      <ContractPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        contract={marketingContract}
        contractType="marketing"
      />
    </div>
  );
};

export default MarketingContractDetails;